import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { getQuestionsById } from '../../../assets/cards/cards';
import { ReactComponent as Lock } from '../../../assets/icons/lock.svg';
import { ReactComponent as Unlocked } from '../../../assets/icons/unlocked.svg';
import { ReactComponent as UnlockedLogo } from '../../../assets/icons/unlocked_logo.svg';
import BottomSheet from '../../../components/bottomsheet/BottomSheet';
import PrimaryButton from '../../../components/buttons/primarybutton/PrimaryButton';
import QuizInteraction from '../../../components/quizinteraction/QuizInteraction';
import QuizQuestionIcon from '../../../components/quizquestionicon/QuizQuestionIcon';
import H1 from '../../../components/text/H1';
import H2 from '../../../components/text/H2';
import { tag } from '../../../services/ATInternetService';
import { addToCollection } from '../../../stored/Collection';
import { hexToRGB } from '../../../utils/Colors';
import DetectedCard from './DetectedCard';
import './LockedCard.sass';
import UnlockedCardMessage from './UnlockedCardMessage';


const LockedCard = ({ card, foundAt, openCollection, onQuizQuestionVisibilityChanged }) => {
	const [doingQuiz, setDoingQuiz] = useState(false);
	const [hasUnlocked, setHasUnlocked] = useState(false);
	const [score, setScore] = useState(0);

	if (hasUnlocked) return <UnlockedCardMessage foundAt={foundAt} card={card} score={score} openCollection={openCollection} />;

	if (doingQuiz) return <QuizCard
		card={card}
		foundAt={foundAt}
		score={score}
		onUnlocked={() => {
			tag.sendClick('quiz', {
				chapter3: card.id,
				name: `collected-${score}`,
			});
			addToCollection(card.id, foundAt, score);
			setHasUnlocked(true);
		}}
		onRightAnswer={() => {
			const newScore = score + 1;
			setScore(newScore);
		}}
		onQuizQuestionVisibilityChanged={onQuizQuestionVisibilityChanged}
	/>;

	return <DetectedCard
		cardId={card.id}
		color={card.colors.primary}
		displayName={card.displayName}
		onUnlockPressed={() => {
			tag.sendClick('quiz', {
				chapter3: card.id,
				name: 'quiz-started',
			});
			setDoingQuiz(true);
		}}
	/>;
};

export default LockedCard;

LockedCard.propTypes = {
	foundAt: PropTypes.string.isRequired,
	openCollection: PropTypes.func.isRequired,
	card: PropTypes.shape({
		colors: PropTypes.shape({
			primary: PropTypes.any
		}),
		displayName: PropTypes.any,
		id: PropTypes.any
	}).isRequired,
	onQuizQuestionVisibilityChanged: PropTypes.func.isRequired
};

export const QuizCard = ({ card, onQuizQuestionVisibilityChanged, onUnlocked, onRightAnswer, score, isUnlocked }) => {
	const questions = getQuestionsById(card.id);

	const [questionIndex, setQuestionIndex] = useState(0);
	const [hasAnswered, setHasAnswered] = useState(false);
	const [lastAnswerValue, setLastAnswerValue] = useState(null);
	const [hasFinished, setHasFinished] = useState(false);

	const questionType = Object.keys(questions)[questionIndex];
	const questionData = questions[questionType].question;

	const getAnswerText = () => {
		return <>
			{`${getAnswerReaction()} `}
			{questionData.answerText.split('{{answer}}')[0]}
			<span className="lockedcard__answer__value text-cadillac">{questionData.answerValue}</span>
			{questionData.answerText.substring(questionData.answerText.indexOf('{{answer}}') + '{{answer}}'.length)}
		</>;
	};

	const checkAnswer = (answer) => {
		if (!questionData?.answerValue || !answer) return '';
		const rightAnswer = answer.toString().toLowerCase().replace('<wbr>', '') === questionData.answerValue.toString().toLowerCase().replace('<wbr>', '');
		if (rightAnswer) {
			onRightAnswer();
			tag.sendClick('quiz', {
				chapter3: card.id,
				name: `right-answer-${questionType}`,
			});
		} else {
			tag.sendClick('quiz', {
				chapter3: card.id,
				name: `wrong-answer-${questionType}`,
			});
		}

	}

	let getAnswerReaction = () => {
		if (!questionData?.answerValue || !lastAnswerValue) return '';

		const rightAnswer = lastAnswerValue.toString().toLowerCase().replace('<wbr>', '') === questionData.answerValue.toString().toLowerCase().replace('<wbr>', '');

		const options = rightAnswer ? [
			'Yesss!',
			'Helemaal goed!',
			'In één keer goed!',
			'Jij weet het!',
			'Juist!',
			'Wat goed!',
		] : [
			'Het zit zo:',
			'Oeps!',
		];

		return options[Math.floor(Math.random() * options.length)];
	}

	useEffect(() => {
		if (hasFinished) setTimeout(() => {
			onUnlocked();
		}, 3500);
	}, [questionIndex, hasAnswered, hasFinished, onUnlocked]);

	useEffect(() => {
		onQuizQuestionVisibilityChanged(!hasAnswered);
	}, [hasAnswered, onQuizQuestionVisibilityChanged]);

	return <BottomSheet>

		{/* eslint-disable-next-line react/no-danger */}
		<style dangerouslySetInnerHTML={{
			__html: `
                .cardcolored path {
                    fill: ${card.colors.primary};
                }
                `}}
		/>

		<UnlockProgress isCardAlreadyUnlocked={isUnlocked} score={score} index={questionIndex} total={Object.keys(questions).length} color={card.colors.primary} animated={hasFinished} />

		{(!hasFinished) ? (
			<>
				<QuizQuestionIcon type={questionType} />

				<div
					className="transition-all px-6 overflow-visible py-4"
					style={{
						opacity: hasAnswered ? 0 : 1,
						transform: hasAnswered ? 'translateY(100%)' : '',
						pointerEvents: hasAnswered ? 'none' : undefined,
						zIndex: hasAnswered ? 0 : 1,
						position: hasAnswered ? 'absolute' : undefined,
					}}>
					<H1 className="mt-12 mb-4 select-none">{questionData.questionText}</H1>
					{!hasAnswered && <QuizInteraction
						type={questionData.answerOptions.type}
						data={questionData.answerOptions.data}
						onAnswered={(answer) => {
							setLastAnswerValue(answer);
							setHasAnswered(true);
							checkAnswer(answer);
						}}
					/>}
				</div>

				<div
					className="transition-all px-6 overflow-visible py-4"
					style={{
						opacity: hasAnswered ? 1 : 0,
						transform: hasAnswered ? '' : 'translateY(100%)',
						pointerEvents: hasAnswered ? undefined : 'none',
						zIndex: hasAnswered ? 1 : 0,
						position: hasAnswered ? undefined : 'absolute',
					}}>
					<div className="text-center">
						<H2 className="mt-12 mb-4 select-none">{getAnswerText()}</H2>
						{hasAnswered && <PrimaryButton
							color={card.colors.primary}
							inverted
							animated="0.2s"
							tabIndex={hasAnswered ? 0 : -1}
							onClick={() => {
								if (questionIndex >= Object.keys(questions).length - 1) {
									setHasFinished(true);
								} else {
									setHasAnswered(false);
									setQuestionIndex(questionIndex + 1);
								}
								tag.sendClick('quiz', {
									chapter3: card.id,
									name: 'next-pressed',
								});
							}}>
							Volgende
						</PrimaryButton>}
					</div>
				</div>
			</>
		) : (
			<div style={{ height: '50vh', minHeight: '20rem', }}>
				<div className="flex justify-center content-center h-full items-center">
					{score === 6 ?
						<H2 className="text-center mt-52 mx-6">Wauw, je hebt alle vragen over {card.plural} goed! </H2>
						:
						<H2 className="text-center mt-52 mx-6">Nu weet je alles over {card.plural}!</H2>}
				</div>
			</div>
		)}
	</BottomSheet>;
};

QuizCard.propTypes = {
	card: PropTypes.any.isRequired,
	onQuizQuestionVisibilityChanged: PropTypes.func.isRequired,
	onRightAnswer: PropTypes.func.isRequired,
	onUnlocked: PropTypes.func.isRequired,
	score: PropTypes.number.isRequired,
	isUnlocked: PropTypes.bool
}

QuizCard.defaultProps = {
	isUnlocked: false
}

const UnlockProgress = ({ animated, color, index, total, isCardAlreadyUnlocked, score }) => {
	const [isUnlocked, setIsUnlocked] = useState(false);

	useEffect(() => {
		if (animated) setTimeout(() => setIsUnlocked(true), 1500);
	}, [animated]);

	return <div
		className={isUnlocked ? 'inline absolute' : 'shadow-xl rounded-full bg-white inline absolute'}
		style={{
			transform: animated ? 'translate(-50%, -50%)' : 'translateY(-3rem)',
			transition: 'all 1000ms cubic-bezier(0.83, 0, 0.17, 1)',
			width: animated ? '10rem' : '6rem',
			height: animated ? '10rem' : '6rem',
			left: animated ? '50%' : '1.5rem',
			top: animated ? 'calc(50% - 2.5rem)' : '0px',
		}}>
		{isCardAlreadyUnlocked ?
			<div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center scale-150">
				<H1
					className="text-center"
					style={{ color }}>{score}<span className="text-xs text-bold">/6</span></H1>
			</div> :
			<>
				<Lock
					style={{
						transition: 'all 500ms ease-in-out',
						transform: `translate(-50%, -50%) scale3d(${animated ? '0, 0, 0' : '1, 1, 1'})`,
					}}
					className="absolute top-1/2 left-1/2 cardcolored"
				/>
				<Unlocked
					style={{
						transition: 'all 500ms ease-in-out',
						transform: `translate(-50%, -50%) scale3d(${(animated && !isUnlocked) ? '1, 1, 1' : '0, 0, 0'})`,
					}}
					className="unlockprogress--unlocked absolute top-1/2 left-1/2 cardcolored"
				/>
				{isUnlocked &&
					<UnlockedLogo
						style={{
							transition: 'all 500ms ease-in-out',
							transform: 'translate(-50%, -50%)',
						}}
						className="unlockprogress--unlockedlogo absolute top-1/2 left-1/2"
					/>}
			</>}

		<div className="transition-all" style={{ opacity: isUnlocked ? 0 : 1 }}>
			<CircularProgressbar
				value={Math.min(animated ? 100 : 90, ((index / total) * 100) + (100 / total))}
				strokeWidth={13.33333}
				styles={buildStyles({
					pathColor: color,
					trailColor: hexToRGB(color, 0.1),
				})}
			/>
		</div>
	</div>;
};

UnlockProgress.propTypes = {
	animated: PropTypes.bool.isRequired,
	color: PropTypes.string.isRequired,
	index: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
	isCardAlreadyUnlocked: PropTypes.bool.isRequired
}
